import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { BlobServiceClient, ContainerClient } from "@azure/storage-blob";
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class BlobStorageService {
  accountName = environment.ACCOUNT_NAME_BLOB_STORAGE;
  containerName = "banner-events";
  sasToken = environment.SAS_TOKEN_BLOB_STORAGE_AZURE;

  constructor(
    private readonly http: HttpClient,
    private sanitizer: DomSanitizer,
  ) {}

  private getBlobServiceClient(sas: string): BlobServiceClient {
    return new BlobServiceClient(
      `https://${this.accountName}.blob.core.windows.net?${sas}`,
    );
  }

  private containerClient(sas: string): ContainerClient {
    return this.getBlobServiceClient(sas).getContainerClient(
      this.containerName,
    );
  }

  async createContainerIfNotExists(
    sas: string,
    containerName: string,
  ): Promise<ContainerClient> {
    const client = this.getBlobServiceClient(sas);
    const containerClient = client.getContainerClient(containerName);

    try {
      const exists = await containerClient.exists();
      if (!exists) {
        await containerClient.create();
      }
      return containerClient;
    } catch (err: any) {
      console.error("Erro ao verificar/criar o container:", err.message);
      throw err;
    }
  }

  uploadBlobToContainer(
    client: ContainerClient,
    content: Blob,
    name: string,
    handler: () => void,
  ) {
    const blockBlobClient = client.getBlockBlobClient(name);
    blockBlobClient
      .uploadData(content, {
        blobHTTPHeaders: { blobContentType: content.type },
      })
      .then(() => handler())
      .catch((err) => {
        console.error("Erro ao fazer upload: ", err.message);
      });
  }

  uploadImage(sas: string, content: Blob, name: string, handler: () => void) {
    const client = this.containerClient(sas);
    this.uploadBlobToContainer(client, content, name, handler);
  }

  async uploadImageAsync(sas: string, content: Blob, name: string): Promise<void> {
    const client = this.containerClient(sas);
    const blockBlobClient = client.getBlockBlobClient(name);

    try {
      await blockBlobClient.uploadData(content, {
        blobHTTPHeaders: { blobContentType: content.type },
      });
      console.log(`Upload successful: ${name}`);
    } catch (err: any) {
      console.error("Erro ao fazer upload: ", err.message);
      throw err;
    }
  }

  async listImages(sas: string): Promise<string[]> {
    const client = this.containerClient(sas);
    return this.listBlobs(client);
  }

  downloadImage(
    sas: string,
    name: string,
    handler: (blob?: Blob, error?: any) => void,
  ) {
    try {
      const client = this.containerClient(sas);
      this.downloadBlob(name, client, handler);
    } catch (error) {
      console.log("Error downloading image", error);
    }
  }

  deleteImage(sas: string, name: string, handler: () => void) {
    const client = this.containerClient(sas);
    this.deleteBlob(name, client, handler);
  }

  private async listBlobs(client: ContainerClient): Promise<string[]> {
    const result: string[] = [];
    const blobs = client.listBlobsFlat();
    for await (const blob of blobs) {
      result.push(blob.name);
    }
    return result;
  }

  private downloadBlob(
    name: string,
    client: ContainerClient,
    handler: (blob?: Blob, error?: any) => void,
  ) {
    const blobClient = client.getBlobClient(name);
    console.log("blobClient-----------", blobClient);
    blobClient
      .download()
      .then((resp) => {
        resp.blobBody?.then((blob) => {
          handler(blob);
        });
      })
      .catch((error) => {
        console.log("Erro ao baixar blob", error);
        handler(undefined, error);
      });
  }

  private deleteBlob(
    name: string,
    client: ContainerClient,
    handler: () => void,
  ) {
    client.deleteBlob(name).then(() => {
      handler();
    });
  }

  async fileExists(sasToken: string, filePath: string): Promise<boolean> {
    const client = this.containerClient(sasToken);
    const blobClient = client.getBlockBlobClient(filePath);
    return blobClient.exists();
  }

  async loadEventImageToUrl(event_thumb: string): Promise<string | null> {
    if (event_thumb !== "" && event_thumb !== "FALTA IMPLEMENTAR") {
      return new Promise((resolve, reject) => {
        try {
          this.downloadImage(this.sasToken, event_thumb, (blob, error) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              const trustedUrl = this.sanitizer.bypassSecurityTrustUrl(
                url,
              ) as string;
              resolve(trustedUrl);
            } else {
              reject(error);
            }
          });
        } catch (error) {
          reject(error);
        }
      });
    }
    return null;
  }
}
